/**
 * AvailabilityWindowList — read-only list of manually/import-marked
 * availability windows for a media unit, rendered in the media unit detail
 * Rezerwacje tab alongside the reservations table.
 *
 * Props-only component. No data fetching.
 */

import type { AvailabilityWindowState } from '@ooh/contracts';
import { Badge } from '@ooh/ui/components/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';
import { availabilityWindowStateVariantMap } from '../../lib/availability-window-state';

export interface AvailabilityWindowRow {
  id: number;
  dateFrom: string;
  dateTo: string;
  state: AvailabilityWindowState;
  stateLabel: string;
}

export interface AvailabilityWindowListLabels {
  title: string;
  dateFromLabel: string;
  dateToLabel: string;
  stateLabel: string;
  emptyLabel: string;
  /** Notice shown when `total` exceeds the number of rows actually rendered. */
  truncatedNotice: (shown: number, total: number) => string;
}

export interface AvailabilityWindowListProps {
  windows: AvailabilityWindowRow[];
  labels: AvailabilityWindowListLabels;
  /**
   * Total window count reported by the server (may exceed `windows.length`
   * when the list endpoint's fixed page size truncates the result). Omit or
   * pass a value <= windows.length to skip the truncation notice.
   */
  total?: number;
}

export function AvailabilityWindowList({ windows, labels, total }: AvailabilityWindowListProps) {
  return (
    <div data-testid="availability-window-list" className="mt-6 space-y-2">
      <p className="text-sm font-medium text-foreground">{labels.title}</p>
      {windows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{labels.emptyLabel}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-background">
          <Table className="text-sm">
            <TableHeader>
              <TableRow className="border-b border-border">
                <TableHead className="w-28">{labels.dateFromLabel}</TableHead>
                <TableHead className="w-28">{labels.dateToLabel}</TableHead>
                <TableHead>{labels.stateLabel}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {windows.map((w) => (
                <TableRow key={w.id} data-testid={`availability-window-row-${String(w.id)}`}>
                  <TableCell className="px-3 py-2 font-mono">{w.dateFrom}</TableCell>
                  <TableCell className="px-3 py-2 font-mono">{w.dateTo}</TableCell>
                  <TableCell className="px-3 py-2">
                    <Badge variant={availabilityWindowStateVariantMap[w.state]}>
                      {w.stateLabel}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {total !== undefined && total > windows.length ? (
        <p className="text-xs text-muted-foreground">
          {labels.truncatedNotice(windows.length, total)}
        </p>
      ) : null}
    </div>
  );
}
